import { ChangeDetectionStrategy, Component, computed, inject, input, viewChild } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { Filter } from '../../../../types/filter';
import { FilterOperator } from '../../../../types/filter-operators';
import { PageFilterItem } from '../../../types/page-filter-item';
import { PageFilterService } from '../../../services/page-filter.service';
import { toQueryFilter } from '../../../../helper/to-query-filter.function';
import { excludeFilterNode } from '../../../../helper/exclude-filter-node.function';
import { FilterInputBase } from '../../filter-inputs/filter-input.base';
import { resolveFilterInputComponent } from '../../../helper/resolve-filter-input.function';

@Component({
  selector: 'soft-page-filter-detail',
  imports: [
    NgComponentOutlet
  ],
  templateUrl: './page-filter-detail.component.html',
  styleUrl: './page-filter-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterDetailComponent<T extends object> {
  readonly service = inject(PageFilterService);
  readonly item = input.required<PageFilterItem<T, any>>();
  readonly filter = input.required<Filter<T> | null>();
  readonly operator = input.required<FilterOperator>();

  readonly component = computed(() => resolveFilterInputComponent(this.item(), this.operator()));

  // The input is created dynamically via *ngComponentOutlet, so a viewChild(FilterInputBase)
  // can't match it — read the instance off the outlet directive instead.
  private readonly outlet = viewChild(NgComponentOutlet);

  getFilterInput(): FilterInputBase<T, FilterOperator> | null {
    return (this.outlet()?.componentInstance as FilterInputBase<T, FilterOperator>) ?? null;
  }

  readonly dataService = computed(() => this.item().source.dataService);

  readonly queryFilter = computed(() => {
    const item = this.item();
    const filter = this.filter();
    const state = this.service.states().find(o => o.config === item.source.state.config);
    if(!state)
      return null;
    let globalFilter = state.state();
    if(!globalFilter)
      return null;
    if(filter)
      globalFilter = excludeFilterNode(globalFilter, filter.uuid);
    if(!globalFilter)
      return null;
    return toQueryFilter(globalFilter, state.config)
  }, { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) });
}
